'use client';

import { useState, type ComponentProps } from 'react';

import { useTranslation } from 'react-i18next';

import { currencyMinorUnits, formatCurrency } from '@kit/shared/currency';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import {
  annualRateSavings,
  groupMembershipRates,
} from '../_lib/group-membership-rates';
import type { AvailableMembershipPlan } from '../_lib/server/available-memberships.loader';
import { NativePurchaseMembershipCard } from './native-purchase-membership-card';

type CardProps = ComponentProps<typeof NativePurchaseMembershipCard>;

export function MembershipRateCards({
  plans,
  selectedPlanId,
  onSelect,
  cardProps,
}: {
  plans: AvailableMembershipPlan[];
  selectedPlanId?: string;
  onSelect?: (plan: AvailableMembershipPlan) => void;
  cardProps?: (plan: AvailableMembershipPlan) => Partial<CardProps>;
}) {
  return groupMembershipRates(plans).map((group) => (
    <MembershipRateCard
      key={group.key}
      plans={group.plans}
      selectedPlanId={selectedPlanId}
      onSelect={onSelect}
      cardProps={cardProps}
    />
  ));
}

function MembershipRateCard({
  plans,
  selectedPlanId,
  onSelect,
  cardProps,
}: ComponentProps<typeof MembershipRateCards>) {
  const tokens = useNativeTokens();
  const { i18n } = useTranslation();
  const currency = useTenantCurrency();
  const [chosenId, setChosenId] = useState(
    plans.find((plan) => plan.period === 'monthly')?.id ?? plans[0]!.id,
  );
  const plan =
    plans.find((rate) => rate.id === selectedPlanId) ??
    plans.find((rate) => rate.id === chosenId) ??
    plans[0]!;
  const savings = annualRateSavings(plans);
  const purchaseProps = cardProps?.(plan);
  const money = (amount: number) => (
    <span className="inline-flex items-baseline gap-1 tabular-nums">
      <CurrencyIcon className="h-3.5 w-3.5 self-center" />
      {formatCurrency(amount, currency, i18n.language, {
        style: 'decimal',
        maximumFractionDigits: currencyMinorUnits(currency),
      })}
    </span>
  );

  return (
    <NativePurchaseMembershipCard
      {...purchaseProps}
      plan={plan}
      selectable={Boolean(onSelect)}
      selected={selectedPlanId === plan.id}
      onSelect={() => onSelect?.(plan)}
      rateOptions={plans.length > 1 ? (
        <div style={{ marginTop: 20 }} data-test="membership-rate-options">
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: plans.length === 4 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))' }}
          >
            {plans.map((rate) => (
              <button
                key={rate.id}
                type="button"
                disabled={purchaseProps?.isPurchasing}
                aria-pressed={rate.id === plan.id}
                data-plan-id={rate.id}
                onClick={(event) => {
                  event.stopPropagation();
                  setChosenId(rate.id);
                  onSelect?.(rate);
                }}
                style={{
                  padding: '14px 10px',
                  minHeight: 44,
                  borderRadius: tokens.radius,
                  background: rate.id === plan.id ? tokens.foreground : 'transparent',
                  color: rate.id === plan.id ? tokens.background : tokens.foreground,
                  border: `1px solid ${tokens.line}`,
                  cursor: purchaseProps?.isPurchasing ? 'default' : 'pointer',
                }}
              >
                <span className="block text-sm">
                  <Trans i18nKey={`common:memberClub.memberships.rates.${rate.period}`} />
                </span>
                <span className="mt-1 block font-medium">{money(rate.price)}</span>
                <span className="mt-1 block text-xs">
                  <Trans i18nKey={`common:memberClub.memberships.rates.pricePeriod.${rate.period}`} />
                </span>
              </button>
            ))}
          </div>
          {savings !== null ? (
            <p className="mt-3 text-sm">
              <Trans i18nKey="common:memberClub.memberships.rates.savings" />{' '}{money(savings)}
            </p>
          ) : null}
          {plan.period === 'annual' ? (
            <p className="mt-2 text-xs" style={{ color: tokens.muted }}>
              {money(plan.price / 12)}{' '}
              <Trans i18nKey="common:memberClub.memberships.rates.equivalent" />
            </p>
          ) : null}
        </div>
      ) : undefined}
    />
  );
}
